<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { tConversation } from '@/plugins/i18n';
import type { ExternalTransactionKind } from '@/common/bot/enum/ExternalTransactionType';
import type { ConnectToAgent } from '@/common/bot/types/config';
import TransactionList from './TransactionList.vue';
import { usePaymentTransactions } from './composables/usePaymentTransactions';
import { useTransactionMetadata } from './composables/useTransactionMetadata';
import { useMetadataRules } from './composables/useMetadataRules';
import type { TransactionSelectEvent, TransactionSubmitEvent } from './types';
import { TransStatement } from '@/common/api/model';
import { transactionParamsSwitch } from '@/common/sb-transaction.helpers';
import { TransactionType } from '@/common/config';
import { ApplyMetadataAutomationExternEvent } from '@/common/bot/types';
import { ExternalBotEvent } from '@/common/bot/enum';
import { randomId } from '@/common/bot/lib';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

const TRANSACTIONS_PER_PAGE = 3;

export interface PaymentTransactionMessageProps {
  transactionSubtypes: ExternalTransactionKind[];
  connectConfig?: ConnectToAgent | null;
  canSubmit?: boolean;
  cancelable?: boolean;
  preSelectedTransactions?: Array<TransStatement>;
  /** When true, only single transaction can be selected (multi-select blocked). Default false = allow multi-select. */
  singleSelectOnly?: boolean;
}

const props = defineProps<PaymentTransactionMessageProps>();

const emit = defineEmits<{
  (e: 'select', event: TransactionSelectEvent): void;
  (e: 'submit', event: TransactionSubmitEvent): void;
  (e: 'metadata-automation', event: ApplyMetadataAutomationExternEvent): void;
  (e: 'scroll-bottom'): void;
}>();

const { transactions, loading, error, hasMore, loadMore, currentPage } = usePaymentTransactions({
  transactionSubtypes: props.transactionSubtypes,
  pageSize: TRANSACTIONS_PER_PAGE,
});

const { enrichTransactions } = useTransactionMetadata();
const { checkMetadataAutomation } = useMetadataRules();

const isSubmitting = ref(false);
const submitButtonText = computed(() => tConversation('page.guest.button.proceed'));
const multiSelect = computed(() => !props.singleSelectOnly);

// Derive transaction type from subtypes
const transactionType = computed(() => {
  const firstSubtype = props.transactionSubtypes[0];
  if (!firstSubtype) return null;
  const params = transactionParamsSwitch.match(firstSubtype);
  return params?.type as TransactionType.Deposit | TransactionType.Withdrawal | null;
});

const handleSelect = (event: TransactionSelectEvent) => {
  emit('select', event);
};

const handleMetadataAutomation = async (paymentTransactions: TransStatement[]) => {
  // Run metadata automation check for the first selected transaction
  const firstTransaction = paymentTransactions[0];
  const metadataResult = await checkMetadataAutomation(firstTransaction, transactionType.value!, props.connectConfig!, {
    pageSize: TRANSACTIONS_PER_PAGE,
    pageNo: currentPage.value,
  });

  const event: ApplyMetadataAutomationExternEvent = {
    id: randomId(),
    type: ExternalBotEvent.APPLY_METADATA_AUTOMATION,
    payload: {
      supportOptions: props.connectConfig!,
      metadataAutomationMessage: metadataResult?.responseText,
      metadataAutomationRule: metadataResult?.rule,
      transaction: firstTransaction,
      transactionType: transactionType.value!,
    },
  };

  emit('metadata-automation', event);
};

const handleSubmit = async (event: TransactionSubmitEvent) => {
  isSubmitting.value = true;

  try {
    const paymentTransactions = event.transactions as TransStatement[];

    // Check if metadata automation is enabled in connect config
    const hasMetadataRules = props.connectConfig?.metadataRules && props.connectConfig.metadataRules.length > 0;
    const hasTransactions = Boolean(transactionType.value && paymentTransactions.length > 0);

    if (hasMetadataRules && hasTransactions) {
      await handleMetadataAutomation(paymentTransactions);
      return;
    }

    // No metadata automation applied, enrich with standard metadata
    const enrichedTransactions = await enrichTransactions(paymentTransactions);
    emit('submit', {
      ...event,
      transactions: enrichedTransactions,
    });
  } catch (err) {
    logger.error('[PAYMENT_TRANSACTION_MESSAGE] Failed to process transaction metadata', {
      error: parseErrorMessage(err),
    });
    // Fallback: emit with original transactions if processing fails
    emit('submit', event);
  } finally {
    isSubmitting.value = false;
  }
};

// watch for data, once loaded – scroll to bottom
watch(
  transactions,
  () => {
    if (transactions.value.length > 0) {
      emit('scroll-bottom');
    }
  },
  { immediate: true }
);
</script>

<template>
  <TransactionList
    :transactions="transactions"
    :transaction-subtypes="transactionSubtypes"
    :initial-selected-transactions="preSelectedTransactions"
    :show-load-more="hasMore"
    :loading="loading"
    :error="error"
    :is-submitting="isSubmitting"
    :submit-button-text="submitButtonText"
    :multi-select="multiSelect"
    :can-submit="canSubmit"
    :editable="canSubmit"
    :cancelable="cancelable"
    @select="handleSelect"
    @submit="handleSubmit"
    @load-more="loadMore"
  />
</template>
